// src/IssueParts.js
import { useState } from "react";
import { apiFetch } from "./api";
import QRScannerModal from "./QRScannerModal";

export default function IssueParts({ site }) {
  const [searchBy, setSearchBy] = useState("Part Number");
  const [term, setTerm] = useState("");
  const [scanOpen, setScanOpen] = useState(false);

  const [partInfo, setPartInfo] = useState(null); // {part, uom_display, ...}
  const [qtyIssued, setQtyIssued] = useState("");
  const [reason, setReason] = useState("");

  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [alts, setAlts] = useState([]);

  const loadPart = async (value) => {
    const t = (value ?? term).trim();
    if (!t) return;
  
    setErr("");
    setPartInfo(null);
    setAlts([]); // ✅ clear old alternatives immediately
  
    try {
      const qs = new URLSearchParams({ site, search_by: searchBy, term: t });
      const res = await apiFetch(`/api/issue/part?${qs}`);
      if (!res.ok) throw new Error(await res.text());
  
      const data = await res.json();
      setPartInfo(data);
  
      // ✅ fetch alternatives
      try {
        const qsAlt = new URLSearchParams({
          site,
          part_number: data.part["Part Number"],
        });
        const rAlt = await apiFetch(`/api/issue/alternatives?${qsAlt}`);
        const altList = rAlt.ok ? await rAlt.json() : [];
        setAlts(altList || []);
      } catch {
        setAlts([]);
      }
  
      setQtyIssued("");
      setReason("");
    } catch (e) {
      console.error(e);
      setErr("Part not found.");
      setPartInfo(null);
      setAlts([]); // ✅ clear alternatives if search fails
    }
  };

  const onScan = (text) => {
    const cleaned = (text || "").trim();
    setTerm(cleaned);
    setSearchBy("Part Number");
    loadPart(cleaned);
  };

  const saveIssue = async () => {
    if (!partInfo?.part) return;

    const pn = (partInfo.part["Part Number"] || "").trim();
    if (!pn) return;

    const q = parseFloat(qtyIssued || "0");
    if (!(q > 0)) {
      setErr("Enter Qty to Issue (> 0).");
      return;
    }

    setBusy(true);
    setErr("");

    try {
      const payload = {
        site,
        part_number: pn,
        qty_issued: q,
        reason: reason || "Parts Issue",
      };

      const res = await apiFetch("/api/issue", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const tx = await res.text().catch(() => "");
        throw new Error(tx || `HTTP ${res.status}`);
      }

      const out = await res.json();
      alert(
        `Issued OK.\nPart: ${out.part_number}\nIssued: ${out.issued}\nNew Total: ${out.new_total_quantity}\nBatches: ${out.batch_details}`
      );

      // reload part so on-hand updates
      await loadPart(pn);
    } catch (e) {
      console.error(e);
      setErr(`Issue failed: ${String(e.message || e)}`);
    } finally {
      setBusy(false);
    }
  };

  const p = partInfo?.part;
  const packUom = (partInfo?.received_uom || "").trim();
  const unitUom = (partInfo?.unit_uom || "").trim();

  return (
    <div className="p-5">
      <QRScannerModal open={scanOpen} onClose={() => setScanOpen(false)} onResult={onScan} />

      <h2 className="text-2xl font-semibold mb-4">Issue Parts @ {site}</h2>
      {err && <p className="mb-3 text-red-600">{err}</p>}

      {/* Search */}
      <div className="border rounded-lg p-4 bg-white shadow-sm mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-sm text-gray-600">Search By</label>
            <select className="border rounded px-2 py-2 w-full" value={searchBy} onChange={(e) => setSearchBy(e.target.value)}>
              <option>Part Number</option>
              <option>Manufacturer Part#</option>
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="text-sm text-gray-600">Search</label>
            <input
              className="border rounded px-2 py-2 w-full"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Enter part number or scan QR"
              onKeyDown={(e) => e.key === "Enter" && loadPart()}
            />
          </div>

          <div className="flex gap-2">
            <button className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700" onClick={() => loadPart()}>
              Search
            </button>
            <button className="px-3 py-2 rounded bg-gray-200 hover:bg-gray-300" onClick={() => setScanOpen(true)}>
              Scan QR
            </button>
          </div>
        </div>
      </div>

      {/* Part details */}
      {p && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Left: Part details */}
          <div className="border rounded-lg p-4 bg-white shadow-sm">
            <div className="font-mono font-semibold">{p["Part Number"]}</div>
            <div className="text-gray-700">{p.Description}</div>
      
            <div className="text-sm text-gray-700 mt-2">
              <b>On Hand:</b> {p["Total Quantity"]} &nbsp; | &nbsp;
              <b>UOM Type:</b> {partInfo.uom_display}
            </div>
      
            <div className="text-sm text-gray-700 mt-2">
              <b>Loc:</b> {p.Location || "-"} &nbsp;
              <b>Shelve:</b> {p.Shelve || "-"} &nbsp;
              <b>Row:</b> {p.Row || "-"}
            </div>
      
            <div className="mt-2 text-red-600 font-semibold text-sm">
              ⚠️ This part is being transacted as: {partInfo.uom_display}
            </div>
          </div>
      
          {/* Right: Alternative parts */}
          <div className="border rounded-lg p-4 bg-white shadow-sm">
            <div className="text-lg font-semibold mb-2">Alternative Parts</div>
      
            {alts.length === 0 ? (
              <div className="text-sm text-gray-600">No alternatives found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="border p-2 bg-gray-100 text-left">Part Number</th>
                      <th className="border p-2 bg-gray-100 text-left">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alts.map((a) => (
                      <tr
                        key={a.part_number}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          // click alternative -> load it
                          setSearchBy("Part Number");
                          setTerm(a.part_number);
                          loadPart(a.part_number);
                        }}
                      >
                        <td className="border p-2 font-mono">{a.part_number}</td>
                        <td className="border p-2">{a.total_quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      
            <div className="text-xs text-gray-500 mt-2">
              Tap an alternative to load it.
            </div>
          </div>
        </div>
      )}


      {/* Issue form */}
      {p && (
        <div className="border rounded-lg p-4 bg-white shadow-sm">
          <h3 className="text-lg font-semibold mb-3">Issue Entry</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-gray-600">
                Qty to Issue {unitUom ? `(${unitUom})` : ""}
              </label>
              <input
                className="border rounded px-2 py-2 w-full"
                value={qtyIssued}
                onChange={(e) => setQtyIssued(e.target.value)}
                inputMode="decimal"
                placeholder="e.g. 1.5"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Reason</label>
              <input
                className="border rounded px-2 py-2 w-full"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Parts Issue"
              />
            </div>
          </div>

          <div className="mt-4">
            <button
              className="px-4 py-2 rounded bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
              onClick={saveIssue}
              disabled={busy}
            >
              {busy ? "Issuing…" : "Issue Part"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
